import { createFileRoute, useRouter } from '@tanstack/react-router';
import { gql } from 'shared/__generated__';
import { TWLink } from '../../components/link';
import { genCDN } from 'shared/cdn';
import PerfImageFromFile from '../../components/by-type/images/perf-image-from-file';
import { PerfImageFromFileFragment } from 'shared/__generated__/graphql';
import { useAutoSetTheme } from 'shared/misc/providers/ThemeContext';
import { ProfileThemeType } from 'shared/__generated__/graphql';
import InlineSubscribeToSignUp from '../../components/inline-subscribe-to-sign-up';
import { useAuth } from 'shared/misc/hooks/useAuth';
import { useBreakpoint } from 'shared/misc/providers/breakpoint-provider';

export const Route = createFileRoute('/links/')({
  component: RouteComponent,
  staleTime: 1000 * 60 * 60 * 24,
  head: () => ({
    meta: [
      {
        title: 'PI.FYI',
        description: "A taste of someone's taste.",
        openGraph: {
          images: [
            'https://files.pi.fyi/cdn-cgi/image/quality=50,format=auto/app-info-screenshot.png',
          ],
        },
      },
    ],
  }),
  loader: async ({ context }) => {
    const { data } = await context.apolloClient.query({
      query: gql(/* GraphQL */ `
        query getRecentArticle {
          articleConnection(first: 5) {
            edges {
              node {
                ...ArticleTile
              }
            }
          }

          topFeatureConnection: featureConnection(first: 12, sortBy: "score")
            @connection(key: "topFeatures") {
            pageInfo {
              endCursor
              hasNextPage
            }
            edges {
              node {
                subtitle
                ...FeatureTile
              }
            }
          }
        }
      `),
    });

    const recentArticles = data.articleConnection.edges.map((edge) => edge.node);
    const topFeatures = data.topFeatureConnection.edges.map((edge) => edge.node);

    return {
      recentArticles,
      topFeatures,
    };
  },
});

function RouteComponent() {
  const { recentArticles, topFeatures } = Route.useLoaderData();

  const [recentArticle, ...restOfArticles] = recentArticles;

  console.log(recentArticle);

  const links = [
    {
      title: `Latest: ${recentArticle.document?.title}` || 'Latest Article',
      subtitle: recentArticle.document?.subtitle,
      file: recentArticle.document?.thumbnailFile,
      href: `/p/${(recentArticle?.document?.urlSlug || recentArticle).urlSlug}`,
    },
    {
      title: 'Merch Shop',
      subtitle: 'Perfectly Imperfect t-shirts, hoodies, hats, and more.',
      fileKey: 'charli-t-shirt.png',
      file: null,
      href: 'https://shop.perfectlyimperfect.fyi/shop',
    },
    {
      title: `May 5th: Shy's Burgers x PI`,
      subtitle: 'One night only. Reserve your spot now.',
      fileKey: 'shy-burger-event-1.png',
      href: 'https://www.exploretock.com/funnybar/experience/private/51d96cfa-5c22-4d69-8ab0-cbc769754485?date=2025-05-05&size=2&time=20%3A00',
    },
    {
      title: 'Download the PI.FYI app',
      subtitle: 'A social network for your taste. Over 60,000 users.',
      fileKey: 'app-info-screenshot.png',
      href: 'https://apps.apple.com/us/app/pi-fyi-by-perfectly-imperfect/id6474037926',
    },
    {
      title: 'Browse the archive',
      subtitle:
        'Over 600 interviews with legends like Charli XCX, Francis Ford Coppola, The Rizzler, and more.',
      fileKey: 'pi-archive.png',
      href: '/read',
    },
    {
      title: 'YouTube',
      subtitle: 'A video series of us trying recommendations from guests, users, and more.',
      fileKey: 'youtube-video.png',
      file: null,
      href: 'https://www.youtube.com/watch?v=B53GTgB0bG0',
    },
    {
      title: 'Instagram',
      subtitle: 'Follow us on Instagram to stay up to date.',
      fileKey: 'instagram-screenshot.png',
      file: null,
      href: 'https://www.instagram.com/perfectlyimperfect',
    },
    {
      title: 'Sign up for PI.FYI',
      subtitle: 'Curate, discover, and share your taste with real people.',
      fileKey: 'adaptive-icon.png',
      href: '/sign-up',
    },
    {
      title: 'PI x NYTimes',
      subtitle: 'Click here to read',
      fileKey: 'nytimes-profile.webp',
      file: null,
      href: 'https://www.nytimes.com/2024/01/21/style/perfectly-imperfect-newsletter.html',
    },
    {
      title: 'PI x The Verge',
      subtitle: 'Click here to read',
      fileKey: 'the-verge-profile.webp',
      file: null,
      href: 'https://www.theverge.com/creators/24054294/perfectly-imperfect-pi-fyi-app-tyler-bainbridge',
    },
    {
      title: 'PI x Vanity Fair',
      subtitle: 'Click here to read',
      fileKey: 'vanity-fair-profile.png',
      file: null,
      href: 'https://www.vanityfair.com/style/2022/01/01/perfectly-imperfect-and-the-growing-currency-of-the-personal-recommendation',
    },
    ...restOfArticles.map((article) => ({
      title: article.document?.title || 'Latest Article',
      subtitle: article.document?.subtitle,
      file: article.document?.thumbnailFile,
      href: `/p/${article.document?.urlSlug || article.urlSlug}`,
    })),
    ...topFeatures.map((feature) => ({
      title: (feature.title || '').replace(/#\d+:\s*/, ''),
      subtitle: feature.subtitle,
      file: feature.thumbnailFile?.file,
      href: `/p/${feature.urlSlug}`,
    })),
  ];

  const auth = useAuth();

  const breakpoint = useBreakpoint();

  const isMobile = ['sm', 'base'].includes(breakpoint);

  return (
    <div className="h-dvh w-[100%] items-center justify-center bg-[url('https://files.pi.fyi/star-background.png')] bg-cover bg-center">
      <div className="flex flex-col gap-[12px] justify-center pt-[24px] sm:pt-[48px] max-w-full w-full">
        <img
          src="/static/logo-horizontal.svg"
          width={isMobile ? 300 : 600}
          height={isMobile ? 60 : 120}
          alt="Logo"
          className="self-center invert"
        />
        <div className="flex flex-col gap-[12px] self-center p-[12px] sm:p-[24px] sm:w-[800px]">
          {!auth.user && (
            <InlineSubscribeToSignUp
              autoOpenModalVersion
              // withBorder={false}
              // withPadding={false}
              ctaText={
                <span className="text-[26px] sm:text-[36px] pb-[0px] sm:pb-[8px]">Subscribe</span>
              }
              ctaSubtitle={
                <div className="flex flex-col gap-[12px] sm:gap-[12px] max-w-[600px]">
                  <span>
                    Over <span className="text-brand-highlight">120,000 readers</span> +{' '}
                    <span className="text-brand-highlight">60,000 app users</span> build their taste
                    with PI.FYI.
                  </span>
                  <span className="hidden sm:block ml-[3px] sm:ml-0 text-brand-highlight">
                    Long live human curation!
                  </span>
                </div>
              }
            />
          )}
          {links.map((link) => (
            <LinkItem
              key={link.href}
              title={link.title}
              fileKey={link.fileKey}
              file={link.file}
              href={link.href}
              subtitle={link.subtitle}
              subtitle2={link.subtitle2}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function LinkItem({
  title,
  imageUrl,
  file,
  fileKey,
  subtitle,
  subtitle2,
  href,
}: {
  title: string;
  imageUrl: string;
  file?: PerfImageFromFileFragment;
  fileKey?: string;
  subtitle?: string;
  subtitle2?: string;
  href: string;
}) {
  useAutoSetTheme(ProfileThemeType.PiClassic);

  const isInternal = href.startsWith('/');

  const router = useRouter();

  return (
    <div
      className="w-full border-[1px] border-main border-dashed p-[12px] flex flex-row gap-[12px] sm:gap-[24px] cursor-pointer"
      onClick={() => {
        if (isInternal) {
          router.navigate({ to: href });
        } else {
          window.open(href, '_blank');
        }
      }}
    >
      <PerfImageFromFile
        file={file! || { key: fileKey!, isUploaded: true }}
        alt={title}
        // src={imageUrl}
        height={50}
        width={50}
        className="w-[75px] h-full object-cover aspect-square object-top cursor-pointer"
      />
      <div className="flex flex-col gap-[4px] overflow-hidden">
        <div className="typography-heading-sm sm:typography-heading-lg text-brand-highlight line-clamp-3">
          {title}
        </div>
        {subtitle && (
          <div className="typography-action-sm sm:typography-heading-sm line-clamp-2">
            {subtitle}
          </div>
        )}
        {subtitle2 && (
          <div className="typography-action-sm sm:typography-action-sm line-clamp-2">
            {subtitle2}
          </div>
        )}
      </div>
    </div>
  );
}
